import { Component } from '@theme/component';
import { getScrollTop, scrollTo, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * How far the page has to be scrolled before the button appears, in pixels. Matches the threshold
 * on the current live site (feestwinkel.be reveals its button at scrollTop > 500). A fixed length
 * on purpose — a viewport-relative threshold grows with the screen and hides the button until far
 * too late on a tall desktop display.
 */
const REVEAL_THRESHOLD = 500;

/**
 * @typedef {Object} BackToTopRefs
 * @property {HTMLButtonElement} button - The floating scroll-to-top button.
 */

/**
 * A custom element that reveals a floating "back to top" button once the page has been scrolled
 * past `REVEAL_THRESHOLD`, and scrolls back to the top when the button is activated.
 *
 * Reads scroll position through `@theme/scroll-container` rather than `window`: in Horizon's
 * squeeze layout (≥990px) `html` and `body` are `overflow: hidden` and `.page-wrapper` is the
 * scroll container, so `window.scrollY` is always 0 there. The container swaps at the breakpoint,
 * so the listener is rebound on `scrollContainerMediaQuery` changes — same pattern as `header.js`.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    // The page can load already scrolled (restored position, or a #fragment link).
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#unbindScrollTarget();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);

    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #bindScrollTarget() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #unbindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  }

  /** Crossing 990px swaps the scroll container between `.page-wrapper` and the document. */
  #handleBreakpointChange = () => {
    this.#unbindScrollTarget();
    this.#bindScrollTarget();
    this.#update();
  };

  /** Scroll fires far more often than the page paints — coalesce to one read per frame. */
  #handleScroll = () => {
    if (this.#frame != null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    this.refs.button.toggleAttribute('data-visible', getScrollTop() > REVEAL_THRESHOLD);
  }

  /**
   * Scrolls back to the top of the page. Honours `prefers-reduced-motion` by jumping rather than
   * animating — `behavior` here also overrides the `scroll-behavior: smooth` the scroll container
   * carries in CSS.
   */
  scrollToTop() {
    scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

    // Scrolling does not move focus, so keyboard and screen reader users would otherwise resume
    // tabbing from the footer. Send focus back to the top of the page instead.
    const skipTarget = document.getElementById('MainContent');

    if (skipTarget) {
      skipTarget.setAttribute('tabindex', '-1');
      skipTarget.focus({ preventScroll: true });
      skipTarget.addEventListener('blur', () => skipTarget.removeAttribute('tabindex'), { once: true });
    }
  }
}

if (!customElements.get('back-to-top-component')) {
  customElements.define('back-to-top-component', BackToTopComponent);
}
